import React, { lazy, Suspense, useContext } from "react";
import { useGlobalEnvConfig } from "@environment/GlobalEnvConfigProvider";
import { BasicData } from "@App";
import SpinnerComponent from "@components/spinner";
import SectionContainer from "@containers/section";
import { safeImageUrl, getSliderImages } from "@utils/Utils";
import { Helmet } from "react-helmet";
import { useSelector } from 'react-redux';

import './style.scss';

const AcommodationComponent = lazy(() => import("@components/accommodation"));
const DailyInfoComponent = lazy(() => import("@components/daily-info"));
const DestinationComponent = lazy(() => import("@components/destination"));

const BodyContainer = () => {
  const { REACT_APP_RESOURCE_DOMAIN } = useGlobalEnvConfig();
  const { trip_summary } = useContext(BasicData);
  const bodyData = useSelector((state) => state.bodyData.data);

  const getDestinationImage = (destinations) => {
    const firstDestinationId = trip_summary.legs[0]["destination_id"];
    const firstImage = getSliderImages(destinations[firstDestinationId]?.images)[0];
    return safeImageUrl(
      REACT_APP_RESOURCE_DOMAIN,
      firstImage?.UrlFragment,
      firstImage?.Default.Width,
      firstImage?.Default.Height,
      firstImage?.Default.Mode,
      firstImage?.Default.QueryString
    );
  };

  const getDestinationComponent = (leg, legId, legType, destinationId, startDate, startDay, endDate, endDay, stopIndex) => {

    return (
      <SectionContainer className="section destination" id={`destination-${legId}-${destinationId}${stopIndex >= 0 ? `-${stopIndex}` : ''}`}>
        {bodyData ?
          <Suspense fallback={<SpinnerComponent />}>
            <Helmet>
              <meta property='og:image' content={getDestinationImage(bodyData.destinations)} />
            </Helmet>
            <DestinationComponent id={destinationId} legType={legType} extendedContent={bodyData.destinations} leg={leg} startDate={startDate} startDay={startDay} endDate={endDate} endDay={endDay} />
          </Suspense>
          :
          <SpinnerComponent />
        }
      </SectionContainer>
    );
  }

  const getAccommodationComponent = (leg, accommodationId) => {
    return (
      <SectionContainer className="section accommodation" id={`accommodation-${leg.id}-${accommodationId}`}>
        {bodyData ? (
          <Suspense fallback={<SpinnerComponent />}>
            <AcommodationComponent extendedContent={bodyData.accommodations} leg={leg} />
          </Suspense>
        ) : (
          <SpinnerComponent />
        )}
      </SectionContainer>
    );
  };

  const getDailyInfoComponent = (legId, accommodationId, destinationId, stopIndex, stopEndDay, stopStartDay, name, lastDayLabel) => {
    return (
      <SectionContainer className="daily-info" id={`daily-info-${legId}${stopIndex >= 0 ? `-${stopIndex}` : ""}`}>
        {bodyData ? (
          <Suspense fallback={<SpinnerComponent />}>
            <DailyInfoComponent
              accommodationId={accommodationId}
              extendedContent={bodyData.leg_periods[legId]}
              destinationId={destinationId}
              stopEndDay={stopEndDay}
              stopIndex={stopIndex}
              stopStartDay={stopStartDay}
              name={name}
              accommodationType={bodyData.accommodations[accommodationId]?.type}
              lastDayLabel={lastDayLabel}
            />
          </Suspense>
        ) : (
          <SpinnerComponent />
        )}
      </SectionContainer>
    );
  };

  const renderLegDestinationSection = (leg, showDestinationComponent, legIndex) => {
    return (
      <div style={{ position: 'relative' }} key={legIndex}>
        {showDestinationComponent && getDestinationComponent(leg, leg.id, leg.type, leg.destination_id, leg.start_date, leg.start_day, leg.end_date, leg.end_day)}
        {leg.accommodation_id && getAccommodationComponent(leg, leg.accommodation_id)}
        {getDailyInfoComponent(leg.id, leg.accommodation_id, leg.destination_id, undefined, undefined, undefined, undefined, leg.last_day_label)}
      </div>
    );
  }

  const renderLegStopsSection = (leg, stop, showStopDestinationComponent, legIndex, stopIndex) => {
    return (
      <div style={{ position: 'relative' }} key={`${legIndex}-${stopIndex}`}>
        {showStopDestinationComponent && getDestinationComponent(leg, leg.id, leg.type, stop.destination_id, stop.start_date, stop.start_day, stop.end_date, stop.end_day, stopIndex)}
        {stopIndex === 0 && leg.accommodation_id && getAccommodationComponent(leg, leg.accommodation_id)}
        {getDailyInfoComponent(leg.id, leg.accommodation_id, stop.destination_id, stopIndex, stop.end_day, stop.start_day, undefined, leg.last_day_label)}
      </div>
    );
  }

  const renderLegAccommodationsSection = (leg, legIndex) => {
    return (
      <div style={{ position: 'relative' }} key={legIndex}>
        {getAccommodationComponent(leg, leg.accommodation_id)}
        {getDailyInfoComponent(leg.id, leg.accommodation_id, undefined, undefined, undefined, undefined, undefined, leg.last_day_label)}
      </div>
    );
  }

  const renderLegsSection = () => {
    let previousDestinationId = null
    return trip_summary?.legs.map((leg, legIndex) => {
        if (leg.destination_id) {
          const showDestinationComponent = (previousDestinationId !== null && previousDestinationId !== leg.destination_id) 
          || previousDestinationId === null
          previousDestinationId = leg.destination_id
          return renderLegDestinationSection(leg, showDestinationComponent, legIndex)
        } else if (leg.stops) {
            return (
              leg.stops.map((stop, stopIndex) => {
              const showStopDestinationComponent = (previousDestinationId !== null && previousDestinationId !== stop.destination_id) 
              || previousDestinationId === null
              previousDestinationId = stop.destination_id
                return renderLegStopsSection(leg, stop, showStopDestinationComponent, legIndex, stopIndex)
              })
            );
        } else if (leg.accommodation_id) {
          return renderLegAccommodationsSection(leg, legIndex)
        }
        else {
          return (
            <div style={{ position: 'relative' }} key={legIndex}>
              {getDailyInfoComponent(leg.id, undefined, undefined, undefined, undefined, undefined, leg.name, leg.last_day_label)}
            </div>
          );
        }
      })
  }

  return (
    <div className="body">
      {renderLegsSection()}
    </div>
  );
}

export default BodyContainer;
